import { provideHttpClient, withFetch, withInterceptors, type HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import {
  type ApplicationConfig,
  inject,
  provideAppInitializer,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideRouter } from '@angular/router';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideAppFooter } from '@sbe-spinoff-client/app-footer';
import { provideAppShell, withNoMatchRedirect } from '@sbe-spinoff-client/app-shell';
import { catchError, switchMap, throwError } from 'rxjs';
import { appRoutes } from './app.routes';
import { AuthService } from '@sbe-spinoff-schoolbell-e/web-shared';
import { ApiService } from './shared/api.service';
import { environment } from '../environments/environment';

// schoolbell-e-web의 app.config.ts와 거의 동일한 패턴 — 다른 점은 401 재시도가 최종
// 실패하면 '/login'(이 앱엔 없음) 대신 schoolbellEWebLoginUrl로 보낸다는 것뿐이다.
const tokenRefreshInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const api = inject(ApiService);

  return next(req).pipe(
    catchError((err: unknown) => {
      if (
        err instanceof HttpErrorResponse &&
        err.status === 401 &&
        !req.url.includes('/auth/refresh')
      ) {
        const currentUserId = auth.session()?.userId;
        return api.refresh().pipe(
          switchMap(({ accessToken, userId }) => {
            if (currentUserId !== undefined && userId !== currentUserId) {
              auth.clearSession();
              window.location.href = `${environment.schoolbellEWebLoginUrl}?return=${encodeURIComponent(window.location.href)}`;
              return throwError(() => err);
            }
            auth.updateAccessToken(accessToken);
            const retried = req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } });
            return next(retried);
          }),
          catchError(() => {
            auth.clearSession();
            return throwError(() => err);
          }),
        );
      }
      return throwError(() => err);
    }),
  );
};

// 부팅 시 로그인 상태 복원 — schoolbell-e-web과 같은 sbe_refresh_token 쿠키(API 도메인
// scope)를 공유하므로, 이미 다른 곳에서 로그인해 둔 사용자는 여기서도 세션이 복원된다.
// 실패해도 예외를 던지지 않는다 — authGuard가 schoolbellEWebLoginUrl로 보낸다.
async function restoreSessionOnBoot(): Promise<void> {
  const auth = inject(AuthService);
  const api = inject(ApiService);

  if (auth.isLoggedIn()) return;
  await api.tryRestoreSessionFromCookie();
}

export const appConfig: ApplicationConfig = {
  providers: [
    // 컴포넌트 대부분 subscribe 콜백이 signal.set()/.update()로 귀결되고, id는
    // route.snapshot.paramMap의 1회성 동기 읽기(구독 아님)라 리팩터 없이 전환 가능함을
    // 감사로 확인. logout.service.ts의 setTimeout은 페이지 이동(location.href) 전
    // fallback 타이머일 뿐이라 CD와 무관. 폼은 전부 Signal Forms로 전환돼(모델 signal
    // 자체가 폼 값) 프리필 후 수동 markForCheck()가 필요한 자리가 남아있지 않다.
    provideZonelessChangeDetection(),
    provideBrowserGlobalErrorListeners(),
    provideAnimationsAsync(),
    provideRouter(appRoutes, withNoMatchRedirect()),
    provideHttpClient(withFetch(), withInterceptors([tokenRefreshInterceptor])),
    provideAppInitializer(restoreSessionOnBoot),
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { subscriptSizing: 'dynamic', appearance: 'outline' },
    },
    // schoolbellEWebLoginUrl은 로그인 URL(.../login)이라 origin만 취해 홈으로 링크한다.
    provideAppFooter({
      linkGroups: [
        { title: '학교종이', links: [{ label: '학교종이', href: new URL(environment.schoolbellEWebLoginUrl).origin }] },
      ],
    }),
    provideAppShell({ showThemeToggle: true }),
  ],
};
